<template>
  <a-drawer
    :title="title"
    width="80%"
    :visible="visible"
    :body-style="{ paddingBottom: '80px' }"
    @close="onClose"
  >
    <a-spin :spinning="loading">
      <div class="drawer-content">
        <!-- 基本信息 -->
        <a-descriptions title="基本信息" :column="2" bordered>
          <a-descriptions-item label="订单编号">{{ detailData.orderNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="支付单号">{{ detailData.payOrderNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="支付渠道">
            <a-tag :color="getPayMethodColor(detailData.payMethod)">
              {{ getPayMethodText(detailData.payMethod) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="支付状态">
            <a-tag :color="getPayStatusColor(detailData.payStatus)">
              {{ getPayStatusText(detailData.payStatus) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="支付类型">
            <a-tag :color="getPayTypeColor(detailData.payType)">
              {{ getPayTypeText(detailData.payType) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="退款状态">
            <a-tag :color="getRefundStatusColor(detailData.refundStatus)">
              {{ getRefundStatusText(detailData.refundStatus) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="是否使用优惠券">
            <a-tag :color="getUseCouponColor(detailData.useCoupon)">
              {{ getUseCouponText(detailData.useCoupon) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag :color="getStatusColor(detailData.status)">
              {{ getStatusText(detailData.status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="执行状态">
            <a-tag :color="getExpireDeductStatusColor(detailData.expireDeductStatus)">
              {{ getExpireDeductStatusText(detailData.expireDeductStatus) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="发放状态">
            <a-tag :color="getIssueStatusColor(detailData.issueStatus)">
              {{ getIssueStatusText(detailData.issueStatus) }}
            </a-tag>
          </a-descriptions-item>
        </a-descriptions>

        <!-- 金额信息 -->
        <a-descriptions title="金额信息" :column="2" bordered style="margin-top: 16px">
          <a-descriptions-item label="支付金额">
            <span class="amount-value">¥{{ formatAmount(detailData.price) }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="优惠券抵扣金额">
            <span class="amount-value discount">-¥{{ formatAmount(detailData.couponDiscount) }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="实际支付金额">
            <span class="amount-value actual">¥{{ formatAmount(detailData.actualPayAmount) }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="优惠券编码">{{ detailData.couponCode || '无' }}</a-descriptions-item>
        </a-descriptions>

        <!-- 续费明细 -->
        <a-card title="续费明细" style="margin-top: 16px" v-if="detailData.items && detailData.items.length">
          <a-table
            :columns="itemColumns"
            :data-source="detailData.items"
            :pagination="false"
            size="small"
            rowKey="id"
            bordered
          >
            <span slot="totalArchiveCapacity" slot-scope="text">
              {{ $formatCapacity(text) }}
            </span>
            <span slot="priceTotal" slot-scope="text">
              ¥{{ $formatCapacity(text) }}
            </span>
            <span slot="priceDiscount" slot-scope="text">
              ¥{{ $formatCapacity(text) }}
            </span>
            <span slot="pricePay" slot-scope="text">
              ¥{{ $formatCapacity(text) }}
            </span>
            <span slot="priceUnit" slot-scope="text">
              ¥{{ $formatCapacity(text) }}
            </span>
          </a-table>
        </a-card>

        <!-- 时间信息 -->
        <a-descriptions title="时间信息" :column="2" bordered style="margin-top: 16px">
          <a-descriptions-item label="创建时间">{{ formatTime(detailData.createTime) }}</a-descriptions-item>
          <a-descriptions-item label="支付时间">{{ formatTime(detailData.payTime) }}</a-descriptions-item>
          <a-descriptions-item label="失效时间">{{ formatTime(detailData.expirationTime) }}</a-descriptions-item>
          <a-descriptions-item label="发放时间">{{ formatTime(detailData.issueTime) }}</a-descriptions-item>
        </a-descriptions>

        <!-- 其他信息 -->
        <a-descriptions title="其他信息" :column="1" bordered style="margin-top: 16px">
          <a-descriptions-item label="三方支付凭证">{{ detailData.payThreeVoucher || '无' }}</a-descriptions-item>
          <a-descriptions-item label="退款单号">{{ detailData.refundOrderNo || '无' }}</a-descriptions-item>
          <a-descriptions-item label="三方退款凭证">{{ detailData.refundThreeVoucher || '无' }}</a-descriptions-item>
        </a-descriptions>
      </div>
    </a-spin>

    <div class="drawer-footer">
      <a-button @click="onClose">关闭</a-button>
    </div>
  </a-drawer>
</template>

<script>
import { getDetail } from '@/api/VR/renewal'
import moment from 'moment'

export default {
  name: 'DetailDrawer',
  data() {
    return {
      visible: false,
      loading: false,
      title: '续费订单详情',
      detailData: {},
      itemColumns: [
        {
          title: '归档总容量',
          dataIndex: 'totalArchiveCapacity',
          width: '150px',
          key: 'totalArchiveCapacity',
          scopedSlots: { customRender: 'totalArchiveCapacity' },
          align: 'center',
        },
        {
          title: '续费周期(天)',
          dataIndex: 'periodDay',
          key: 'periodDay',
          align: 'center',
        },
        {
          title: '单价',
          dataIndex: 'priceUnit',
          key: 'priceUnit',
          scopedSlots: { customRender: 'priceUnit' },
          align: 'center',
        },
        {
          title: '总价',
          dataIndex: 'priceTotal',
          key: 'priceTotal',
          scopedSlots: { customRender: 'priceTotal' },
          align: 'center',
        },
        {
          title: '优惠金额',
          dataIndex: 'priceDiscount',
          key: 'priceDiscount',
          scopedSlots: { customRender: 'priceDiscount' },
          align: 'center',
        },
        {
          title: '实付金额',
          dataIndex: 'pricePay',
          key: 'pricePay',
          scopedSlots: { customRender: 'pricePay' },
          align: 'center',
        },
        {
          title: '归档到期时间',
          dataIndex: 'expireTime',
          key: 'expireTime',
          scopedSlots: { customRender: 'expireTime' },
          align: 'center',
        },
      ],
    }
  },
  methods: {
    show(record) {
      this.visible = true
      this.title = `续费订单详情`
      this.loadDetail(record.id)
    },

    hide() {
      this.visible = false
      this.detailData = {}
    },

    onClose() {
      this.hide()
    },

    async loadDetail(id) {
      this.loading = true
      try {
        const response = await getDetail({
          id,
        })
        if (response.success) {
          this.detailData = response.data || {}
        } else {
          this.$message.error('加载详情失败')
        }
      } catch (error) {
        console.error('加载订单详情失败:', error)
        this.$message.error('加载订单详情失败')
      } finally {
        this.loading = false
      }
    },

    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00'
      return parseFloat(amount).toFixed(2)
    },

    formatTime(time) {
      if (!time) return '-'
      return moment(time).format('YYYY-MM-DD HH:mm:ss')
    },

    getPayMethodText(method) {
      const methodMap = {
        '0': '线上支付',
        '1': '线下支付',
        '2': '企业转账',
      }
      return methodMap[method] || '-'
    },

    getPayMethodColor(method) {
      const colorMap = { '0': 'blue', '1': 'green', '2': 'orange' }
      return colorMap[method] || 'default'
    },

    getPayStatusText(status) {
      const statusMap = { 0: '待支付', 1: '已支付' }
      return statusMap[status] || '-'
    },

    getPayStatusColor(status) {
      const colorMap = { 0: 'orange', 1: 'green' }
      return colorMap[status] || 'default'
    },

    getPayTypeText(type) {
      const typeMap = {
        'ALIPAY': '支付宝',
        'WECHAT': '微信',
        'UNIONPAY': '银联',
      }
      return typeMap[type] || '-'
    },

    getPayTypeColor(type) {
      const colorMap = { 'ALIPAY': 'blue', 'WECHAT': 'green', 'UNIONPAY': 'orange' }
      return colorMap[type] || 'default'
    },

    getRefundStatusText(status) {
      const statusMap = {
        0: '未退款',
        1: '退款中',
        2: '已退款',
        3: '退款失败',
      }
      return statusMap[status] || '-'
    },

    getRefundStatusColor(status) {
      const colorMap = { 0: 'grey', 1: 'orange', 2: 'green', 3: 'red' }
      return colorMap[status] || 'default'
    },

    getUseCouponText(useCoupon) {
      const useCouponMap = { 0: '未使用', 1: '已使用' }
      return useCouponMap[useCoupon] || '-'
    },

    getUseCouponColor(useCoupon) {
      const colorMap = { 0: 'grey', 1: 'green' }
      return colorMap[useCoupon] || 'default'
    },

    getStatusText(status) {
      const statusMap = {
        0: '待支付',
        6: '订单完成',
        7: '订单取消',
      }
      return statusMap[status] || '-'
    },

    getStatusColor(status) {
      const colorMap = { 0: 'orange', 6: 'green', 7: 'red' }
      return colorMap[status] || 'default'
    },

    getExpireDeductStatusText(status) {
      const statusMap = { 0: '未执行', 1: '已执行' }
      return statusMap[status] || '-'
    },

    getExpireDeductStatusColor(status) {
      const colorMap = { 0: 'orange', 1: 'green' }
      return colorMap[status] || 'default'
    },

    getIssueStatusText(status) {
      const statusMap = { 0: '未发放', 1: '已发放' }
      return statusMap[status] || '-'
    },

    getIssueStatusColor(status) {
      const colorMap = { 0: 'orange', 1: 'green' }
      return colorMap[status] || 'default'
    },
  },
}
</script>

<style scoped>
.drawer-content {
  padding: 0;
}

.drawer-footer {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 100%;
  border-top: 1px solid #e9e9e9;
  padding: 10px 16px;
  background: #fff;
  text-align: right;
  z-index: 1;
}

.ant-descriptions {
  margin-bottom: 0;
}

.amount-value {
  font-weight: 600;
  color: #1890ff;
}

.amount-value.actual {
  color: #52c41a;
  font-size: 16px;
}

.amount-value.discount {
  color: #ff4d4f;
}
</style>
